import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useProductState } from '../_shared/productContext.js';
import { marginFields, weightField } from '../_shared/blockFields.js';
import { marginStyle, readWeight } from '../_shared/blockRender.js';
const ALIGN = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
/**
 * Shrine `product_title` config (config-by-config). The title TEXT is bound to `product.title` (edited in
 * the product admin, not the theme — Shrine has no title-text input). The settings are: uppercase, an
 * independent DESKTOP and MOBILE font size + alignment (responsive at 749px), and top/bottom margins.
 *
 * Shrine → our id mapping (ids KEPT, never renamed): uppercase_title→uppercase, desktop_size→desktopSize,
 * alignment→alignment, mobile_size→mobileSize, mobile_alignment→mobileAlignment, margin_top→marginTop,
 * margin_bottom→marginBottom. Shrine's mobile_alignment values (mobile-left/-center/-right) collapse to our
 * left/center/right (fed straight into `text-align`; equivalent render). All 7 Shrine settings are present;
 * this round only realigns the Shrine margin defaults (15/15, were 0/0) for config-by-config parity. Shrine's
 * Desktop/Mobile/Margins are header dividers, not inputs — represented here by field `group`s.
 */
export const titleBlockSchema = {
    type: 'title',
    name: 'Title',
    max: 1,
    fields: [
        { id: 'uppercase', type: 'checkbox', label: 'Uppercase title', default: false },
        weightField,
        {
            id: 'desktopSize',
            type: 'range',
            label: 'Size',
            group: 'Desktop',
            min: 24,
            max: 60,
            step: 3,
            default: 39,
            unit: 'px',
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Desktop',
            default: 'left',
            options: ALIGN,
        },
        {
            id: 'mobileSize',
            type: 'range',
            label: 'Size',
            group: 'Mobile',
            min: 20,
            max: 50,
            step: 2,
            default: 28,
            unit: 'px',
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Alignment',
            group: 'Mobile',
            default: 'left',
            options: ALIGN,
        },
        ...marginFields(15, 15),
    ],
};
function num(v, d) {
    return typeof v === 'number' ? v : d;
}
/** Product title `<h1>` — independent desktop/mobile size + alignment via a per-block scoped media query. */
const TitleBlock = ({ block }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-pt-${block.id}`;
    const desk = num(bs.desktopSize, 39);
    const mob = num(bs.mobileSize, 28);
    const align = bs.alignment ?? 'left';
    const mAlign = bs.mobileAlignment ?? 'left';
    // "" → keep the theme's bold weight (unchanged); an explicit weight overrides it.
    const weight = readWeight(bs);
    // Desktop size/align as the base; mobile via BOTH `@media` (real viewports) and `@container` (the
    // builder device-preview, which frames width without a viewport change).
    const mobileRule = `.${cls}{font-size:${mob}px;text-align:${mAlign}}`;
    const css = `.${cls}{font-size:${desk}px;text-align:${align}}` +
        `@media(max-width:749px){${mobileRule}}` +
        `@container aqx-page (max-width:749px){${mobileRule}}`;
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("h1", { className: cls, style: {
                    ...marginStyle(bs, 15, 15),
                    fontFamily: 'var(--aqx-theme-heading-font, var(--aqx-theme-font))',
                    fontWeight: weight || 'var(--aqx-theme-fw-bold, 700)',
                    lineHeight: 1.2,
                    letterSpacing: '0.01em',
                    color: 'var(--aqx-scheme-text)',
                    textTransform: bs.uppercase === true ? 'uppercase' : undefined,
                    wordBreak: 'break-word',
                }, children: ps.product.name })] }));
};
export const titleBlock = {
    type: 'title',
    schema: titleBlockSchema,
    Component: TitleBlock,
    meta: { label: 'Title' },
};
//# sourceMappingURL=index.js.map